/**
 * THE STACKING ORDER. ONE FILE, ONE ORDER, EVERY LAYER NAMED.
 *
 * What was here before was not a scale, it was sediment. Measured on 2026-08-11, the app contained
 * simultaneously: forty-one full-screen modal overlays at `z-50`, a sticky site header at `200`,
 * three dialogs bumped to `60`, a file preview at `70`, two overlays at `300`, the shared
 * `Modal.jsx` at `999999` with the comment "Extremely high z-index to ensure visibility", an
 * anchored menu at `1000`, a toast rail at `9999`, a notification dropdown at `9999` and a tour at
 * `11000`. Every one of those numbers was chosen to beat whatever was on top on the day it was
 * written, which is why they no longer describe an order.
 *
 * The consequence the owner hit, on a phone, on the sticker sheet: a modal at 50 sits UNDER the
 * header at 200. A desktop hides that — a centred panel with room to spare starts well below the
 * header's 128px. A phone does not: the same panel is `max-h-[90vh]` of an 844px screen, so its top
 * strip, the one carrying the title and the close button, lands inside the header's box. The
 * buttons render, they are hit-testable in the DOM, and the tap goes to the nav behind them.
 *
 * THE RULE: nothing anywhere may write a raw z-index above `sticky`. Import from here, or use the
 * Tailwind utility of the same name — `tailwind.config.js` builds `z-header`, `z-modal`, `z-dialog`,
 * `z-popup`, `z-toast` and `z-tour` out of this object, so the class and the constant can never
 * drift apart. `zLayers.contract.test.jsx` fails if they do.
 *
 * Gaps between rungs are deliberate: a screen that genuinely needs to sit just above its neighbour
 * has room to do it locally without a new global rung.
 */
export const Z = {
  /** Page content. Anything that only has to beat its own siblings stays here (Tailwind z-0..z-40). */
  content: 0,

  /** Sticky chrome that belongs to the page: sub-nav tab strips, sticky table headers, action bars. */
  sticky: 100,

  /**
   * The site header. `AppHeader`'s `.ah-sticky-outer`. Above the page, below everything that has to
   * be interacted with on top of the page.
   */
  header: 200,

  /**
   * A full-screen overlay: `fixed inset-0` with a backdrop. Above the header — that is the whole
   * point of this file. This is where every one of the forty-one `z-50` overlays belongs.
   */
  modal: 1000,

  /**
   * A dialog raised over another dialog — a confirm, a file preview, a version viewer. These were
   * the ones already bumped to 60 and 70 by hand, for exactly this reason.
   */
  dialog: 1200,

  /**
   * A menu, dropdown, combobox or date picker. It must outrank every overlay it can be opened
   * INSIDE, or the fix that lifted modals over the header simply drowns the dropdowns instead.
   * `AnchoredMenu` portals to `document.body` and lives here.
   */
  popup: 2000,

  /** Transient, non-interactive-with-the-page notices: toasts, the offline banner. */
  toast: 3000,

  /**
   * The guided tour. Above everything the tour can point at, because a step has to be able to dim
   * and then spotlight a modal, a dropdown or the header itself. `TourOverlay` adds +1..+3 to this
   * for its own dim, spotlight and card.
   */
  tour: 11000,

  /**
   * A blocking acknowledgement the user must deal with before anything else — `AnnouncementModal`.
   * The one thing that outranks the tour, deliberately: a tour is help, this is a gate.
   */
  blocking: 12000,
}

/** Ascending, for the contract test and for anyone reasoning about "what is above what". */
export const Z_ORDER = [
  'content', 'sticky', 'header', 'modal', 'dialog', 'popup', 'toast', 'tour', 'blocking',
]

export default Z
